import { useEffect } from "react";
import { useRouteError } from "react-router";
import { BookOpen, RefreshCw } from "lucide-react";
import { reportError } from "../lib/monitoring";
import { isChunkLoadError, recoverFromChunkError } from "../lib/chunkRecovery";

/**
 * Route-level error UI for the data router.
 *
 * The app-wide `ErrorBoundary` sits outside `RouterProvider` and so never sees
 * errors the router itself catches — a rejected `lazy()` import lands here, not
 * there. Without this, React Router falls back to its built-in developer page
 * ("Unexpected Application Error! … 💿 Hey developer 👋"), which is what users
 * were shown when a deploy invalidated their chunks.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const isStaleBuild = isChunkLoadError(error);

  useEffect(() => {
    reportError(error, { boundary: "route", staleBuild: isStaleBuild });
  }, [error, isStaleBuild]);

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {isStaleBuild ? (
          <RefreshCw size={36} className="text-accent/40 mx-auto mb-4" />
        ) : (
          <BookOpen size={36} className="text-accent/40 mx-auto mb-4" />
        )}
        <h1
          className="text-primary mb-2"
          style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.4rem", fontWeight: 400 }}
        >
          {isStaleBuild ? "Burrow was updated" : "Something went wrong"}
        </h1>
        <p className="text-muted-foreground" style={{ fontSize: "0.9rem", lineHeight: "1.65" }}>
          {isStaleBuild
            ? "This tab is running an older version of Burrow. Reload to pick up the latest — nothing you've captured is affected."
            : "We hit an unexpected bug and our team has been notified. Try reloading the page."}
        </p>
        <button
          onClick={() => {
            // On a stale build a plain reload can serve the same cached shell,
            // so go through the recovery path; fall back to a reload if it
            // declines (already attempted once this session).
            if (isStaleBuild) {
              void recoverFromChunkError().then((reloading) => {
                if (!reloading) window.location.reload();
              });
            } else {
              window.location.reload();
            }
          }}
          className="mt-5 inline-flex items-center rounded-full bg-accent px-5 py-2 text-accent-foreground"
          style={{ fontSize: "0.85rem" }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}
